import React, { createContext, useContext, useState, useEffect } from 'react';
import MonetizationService, { SUBSCRIPTION_PLANS } from '../services/MonetizationService';
import { useAuth } from './AuthContext';

const MonetizationContext = createContext();

export const MonetizationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  // États principaux
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const monetizationService = MonetizationService.getInstance();

  useEffect(() => {
    if (isAuthenticated) {
      loadInitialData();
    } else {
      // Reset des données si pas authentifié
      setSubscription(null);
      setUsage(null);
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const loadInitialData = async () => {
    await refreshData();
  };

  const refreshData = async () => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoading(true);
      
      const [subscriptionData, usageData] = await Promise.all([
        monetizationService.refreshSubscriptionStatus(),
        monetizationService.refreshUsageStats(),
      ]);
      
      setSubscription(subscriptionData);
      setUsage(usageData);
      
    } catch (error) {
      console.error('Erreur refresh data monétisation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const subscribeToPlan = async (planId) => {
    const result = await monetizationService.subscribeToPlan(planId);
    if (result.success) {
      await refreshData();
    }
    return result;
  };

  const cancelSubscription = async () => {
    const result = await monetizationService.cancelSubscription();
    if (result.success) {
      await refreshData();
    }
    return result;
  };

  const updatePaymentMethod = async () => {
    return await monetizationService.updatePaymentMethod();
  };

  // Calculs des capacités
  const planId = subscription?.plan_id === 'free_trial' ? 'free' : subscription?.plan_id;
  const currentPlan = subscription ? SUBSCRIPTION_PLANS.find(p => p.id === planId) || null : null;
  
  // Vérifications de permissions (async pour la cohérence avec le service)
  const canAskQuestion = async () => {
    const result = await monetizationService.canPerformAction('ask_question');
    return result.allowed;
  };

  const canAddChild = async () => {
    const result = await monetizationService.canPerformAction('add_child');
    return result.allowed;
  };

  const canViewFullHistory = async () => {
    const result = await monetizationService.canPerformAction('view_history');
    return result.allowed;
  };

  const canExportData = async () => {
    const result = await monetizationService.canPerformAction('export_data');
    return result.allowed;
  };

  const canViewAdvancedStats = async () => {
    const result = await monetizationService.canPerformAction('advanced_stats');
    return result.allowed;
  };

  const canModifyChild = async () => {
    const result = await monetizationService.canPerformAction('modify_child');
    return result.allowed;
  };

  // Vérification synchrone pour l'UI (basée sur les données locales)
  const canAskQuestionSync = () => {
    if (!currentPlan || !usage) return false;
    if (currentPlan.limits.questionsPerDay === 'unlimited') return true;
    const dailyLeft = Math.max(0, currentPlan.limits.questionsPerDay - (usage.questionsToday || 0));
    const bonus = usage.bonusQuestions || 0;
    return (dailyLeft + bonus) > 0;
  };

  const canAddChildSync = () => {
    if (!currentPlan || !usage) return false;
    const childrenLeft = currentPlan.limits.childProfiles === 'unlimited'
      ? 'unlimited'
      : Math.max(0, currentPlan.limits.childProfiles - (usage.childrenCount || 0));
    return childrenLeft === 'unlimited' || childrenLeft > 0;
  };

  // Statistiques calculées
  const bonusQuestions = usage?.bonusQuestions || 0;
  const questionsLeft = currentPlan?.limits.questionsPerDay === 'unlimited' 
    ? 'unlimited' 
    : currentPlan 
    ? Math.max(0, currentPlan.limits.questionsPerDay - (usage?.questionsToday || 0)) + bonusQuestions
    : 0;

  const questionsUsedToday = usage?.questionsToday || 0;
  const trialDaysLeft = subscription?.trial_days_remaining || 0;
  
  const childrenLeft = currentPlan?.limits.childProfiles === 'unlimited'
    ? 'unlimited'
    : currentPlan
    ? Math.max(0, currentPlan.limits.childProfiles - (usage?.childrenCount || 0))
    : 0;

  // Utilitaires
  const formatPrice = (plan) => {
    if (plan.price === 0) return 'Gratuit';
    
    const price = plan.price.toFixed(2);
    const duration = plan.duration === 'monthly' ? '/mois' : '/an';
    
    if (plan.originalPrice && plan.savings) {
      return `${price}€${duration} (au lieu de ${plan.originalPrice.toFixed(2)}€)`;
    }
    
    return `${price}€${duration}`;
  };

  const getUpgradeMessage = (action) => {
    const messages = {
      ask_question: 'Passez à Explorateur pour répondre à toutes ses questions !',
      add_child: 'Passez à Premium pour ajouter plus de profils d\'enfants !',
      export_data: 'L\'export de données est réservé aux abonnés Premium !',
      advanced_stats: 'Les statistiques avancées sont réservées aux abonnés Premium !',
    };
    return messages[action] || 'Passez à Premium pour débloquer cette fonctionnalité !';
  };

  // États booléens
  const isPremium = subscription?.status === 'active' && currentPlan?.id !== 'free' && currentPlan?.id !== 'free_trial' && currentPlan !== null;
  const isFamily = currentPlan?.id?.includes('family');
  const isYearly = currentPlan?.duration === 'yearly';
  const isTrial = subscription?.status === 'trial';
  const canAccessQuiz = isPremium; // Les utilisateurs gratuits n'ont pas accès aux révisions

  // Données d'usage des histoires (nouvelles)
  const storiesThisMonth = usage?.storiesThisMonth || 0;
  const maxStories = usage?.maxStories || 0;
  const canGenerateStory = usage?.canGenerateStory || false;
  const storiesRemaining = usage?.storiesRemaining || 0;
  const bonusStories = usage?.bonus_stories || 0;
  const totalStoriesRemaining = usage?.total_remaining || storiesRemaining;

  // Compatibilité avec l'ancienne API
  const checkAccess = () => {
    return isPremium || isTrial;
  };

  const upgradeToPremium = async () => {
    // Redirige vers la page d'abonnement
    window.location.href = '/subscription';
  };

  const value = {
    // États
    subscription,
    currentPlan,
    usage,
    isLoading,
    
    // Capacités (versions synchrones pour l'UI)
    canAskQuestion: canAskQuestionSync(),
    canAddChild: canAddChildSync(),
    canViewFullHistory: isPremium || (isTrial && currentPlan?.limits.historyDays !== undefined),
    canExportData: isPremium,
    canViewAdvancedStats: isPremium,
    
    // Capacités (versions async pour vérification précise)
    canAskQuestionAsync: canAskQuestion,
    canAddChildAsync: canAddChild,
    canViewFullHistoryAsync: canViewFullHistory,
    canExportDataAsync: canExportData,
    canViewAdvancedStatsAsync: canViewAdvancedStats,
    canModifyChildAsync: canModifyChild,
    
    // Statistiques
    questionsLeft,
    questionsUsedToday,
    bonusQuestions,
    trialDaysLeft,
    childrenLeft,
    
    // Statistiques histoires (nouvelles)
    storiesThisMonth,
    maxStories,
    canGenerateStory,
    storiesRemaining,
    bonusStories,
    totalStoriesRemaining,
    
    // Actions
    refreshData,
    subscribeToPlan,
    cancelSubscription,
    updatePaymentMethod,
    
    // Utilitaires
    formatPrice,
    getUpgradeMessage,
    isPremium,
    isFamily,
    isYearly,
    isTrial,
    canAccessQuiz,
    
    // Compatibilité ancienne API
    checkAccess,
    upgradeToPremium,
    refreshStatus: refreshData,
    trialDaysRemaining: trialDaysLeft,
    status: subscription?.status || 'unknown'
  };

  return (
    <MonetizationContext.Provider value={value}>
      {children}
    </MonetizationContext.Provider>
  );
};

export const useMonetization = () => {
  const context = useContext(MonetizationContext);
  if (context === undefined) {
    throw new Error('useMonetization must be used within a MonetizationProvider');
  }
  return context;
};